import React, { useState, useEffect, useCallback } from 'react';
import uploadService from '../services/uploadService';
import indexedDBService from '../services/indexedDBService';
import videoService from '../services/videoService';
import { useUpload } from '../contexts/UploadContext';
import { useToast } from '../contexts/ToastContext';
import './Feeds.css';
import './Uploading.css';

const Uploading = () => {
    const { activeUploads, addUpload, updateUploadProgress, completeUpload } = useUpload();
    const toast = useToast();
    const [serverUploads, setServerUploads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pendingBlobs, setPendingBlobs] = useState([]);
    const [recovering, setRecovering] = useState({});

    useEffect(() => {
        fetchUploads();
        checkPendingBlobs();
        const interval = setInterval(fetchUploads, 5000);
        return () => clearInterval(interval);
    }, []);

    const fetchUploads = async () => {
        try {
            const result = await uploadService.getUploads('uploading');
            if (result.success) {
                setServerUploads(result.uploads);
            }
        } catch (error) {
            console.error('Error fetching uploads:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkPendingBlobs = async () => {
        try {
            const pending = await indexedDBService.getAllPending();
            if (pending.length > 0) {
                setPendingBlobs(pending);
            }
        } catch (error) {
            console.error('Error checking IndexedDB:', error);
        }
    };

    const handleRecover = useCallback(async (item) => {
        setRecovering(prev => ({ ...prev, [item.uploadId]: true }));
        try {
            const { blob, meta } = item;

            // Get a fresh signed upload URL
            const urlResult = await videoService.getUploadUrl(
                meta.awb,
                meta.scanType,
                meta.filename,
                meta.fileSize
            );

            if (!urlResult.success) throw new Error('Failed to get upload URL');

            const { uploadId: newUploadId, signedUrl } = urlResult;

            addUpload({
                uploadId: newUploadId,
                awb: meta.awb,
                scanType: meta.scanType,
                filename: meta.filename,
                progress: 0,
                status: 'uploading'
            });

            await uploadService.uploadToS3(signedUrl, blob, (progress) => {
                updateUploadProgress(newUploadId, progress);
            });

            await completeUpload(newUploadId);

            // Remove the old stale IndexedDB entry and the new one
            await indexedDBService.deleteBlob(item.uploadId);
            await indexedDBService.deleteBlob(newUploadId).catch(() => {});

            setPendingBlobs(prev => prev.filter(p => p.uploadId !== item.uploadId));
            toast.success(`Recovered: ${meta.awb} uploaded successfully`);
        } catch (error) {
            console.error('Recovery error:', error);
            toast.error(`Failed to recover ${item.meta?.awb} — please try again`);
        } finally {
            setRecovering(prev => ({ ...prev, [item.uploadId]: false }));
        }
    }, [addUpload, completeUpload, updateUploadProgress, toast]);

    const handleDismiss = async (uploadId) => {
        await indexedDBService.deleteBlob(uploadId);
        setPendingBlobs(prev => prev.filter(p => p.uploadId !== uploadId));
        toast.info('Recording discarded');
    };

    const handleRecoverAll = async () => {
        for (const item of pendingBlobs) {
            if (!recovering[item.uploadId]) {
                await handleRecover(item);
            }
        }
    };

    // Merge in-progress local uploads with server state
    const allUploads = [...activeUploads];
    serverUploads.forEach(serverUpload => {
        if (!allUploads.find(u => u.uploadId === serverUpload.uploadId)) {
            allUploads.push(serverUpload);
        }
    });

    const formatTime = (dateString) => new Date(dateString).toLocaleString();

    const formatDuration = (startTime) => {
        const diff = Math.floor((new Date() - new Date(startTime)) / 1000);
        if (diff < 60) return `${diff}s`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s`;
        return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
    };

    const formatSize = (bytes) => {
        if (!bytes) return '—';
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    if (loading) {
        return (
            <div className="feeds-page">
                <div className="table-section">
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading uploads...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="feeds-page">
            {/* Recovery Banner */}
            {pendingBlobs.length > 0 && (
                <div className="recovery-banner">
                    <div className="recovery-banner-header">
                        <div className="recovery-banner-title">
                            <i className="fas fa-exclamation-triangle"></i>
                            <div>
                                <strong>{pendingBlobs.length} unsaved recording{pendingBlobs.length > 1 ? 's' : ''} found</strong>
                                <p>These videos were recorded but not uploaded — likely due to a browser crash or connection loss. You can recover them now.</p>
                            </div>
                        </div>
                        {pendingBlobs.length > 1 && (
                            <button className="btn btn-primary btn-sm" onClick={handleRecoverAll}>
                                <i className="fas fa-upload"></i> Recover All
                            </button>
                        )}
                    </div>
                    <div className="recovery-items">
                        {pendingBlobs.map(item => (
                            <div key={item.uploadId} className="recovery-item">
                                <div className="recovery-item-info">
                                    <span className="recovery-awb">{item.meta?.awb}</span>
                                    <span className={`scan-badge ${item.meta?.scanType}`}>{item.meta?.scanType}</span>
                                    <span className="recovery-size">{formatSize(item.meta?.fileSize)}</span>
                                    <span className="recovery-date">Saved {new Date(item.savedAt).toLocaleString()}</span>
                                </div>
                                <div className="recovery-item-actions">
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() => handleRecover(item)}
                                        disabled={recovering[item.uploadId]}
                                    >
                                        {recovering[item.uploadId]
                                            ? <><i className="fas fa-spinner fa-spin"></i> Uploading...</>
                                            : <><i className="fas fa-upload"></i> Upload</>
                                        }
                                    </button>
                                    <button
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => handleDismiss(item.uploadId)}
                                        disabled={recovering[item.uploadId]}
                                    >
                                        Discard
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Active Uploads */}
            <div className="table-section">
                {allUploads.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-cloud-upload-alt" style={{ fontSize: '48px', opacity: 0.2, marginBottom: '16px' }}></i>
                        <p>No active uploads</p>
                        <p className="empty-hint">All your videos have been uploaded successfully</p>
                    </div>
                ) : (
                    <div className="uploads-list">
                        {allUploads.map(upload => (
                            <div key={upload.uploadId} className="upload-card">
                                <div className="upload-header">
                                    <div className="upload-awb">{upload.awb}</div>
                                    <span className={`scan-badge ${upload.scanType}`}>
                                        {upload.scanType}
                                    </span>
                                </div>

                                <div className="upload-details">
                                    <div className="detail-row">
                                        <span className="detail-label">Filename:</span>
                                        <span className="detail-value">{upload.filename}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Started:</span>
                                        <span className="detail-value">{formatTime(upload.startTime)}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Duration:</span>
                                        <span className="detail-value">{formatDuration(upload.startTime)}</span>
                                    </div>
                                </div>

                                <div className="upload-progress-bar">
                                    <div className="progress-track">
                                        <div
                                            className="progress-fill"
                                            style={{ width: `${upload.progress || 0}%` }}
                                        ></div>
                                    </div>
                                    <span className="progress-text">{upload.progress || 0}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Uploading;
